(function () {
  'use strict';

  // Prevent double initialization
  if (typeof window !== 'undefined' && window.LoadingOverlayManager) {
    console.warn('LoadingOverlayManager already initialized');
    return;
  }

  const LoadingOverlayManager = (function () {
    const overlays = new Map(); // id -> {el, target, opts, timeoutId, originalPosition}
    const animationDuration = 500; // Match CSS transition duration
    let nextId = 1;

    //
    // Default options
    //

    const defaults = {
      timeout: 0, // ms, 0 = no auto-hide
      message: '', // Optional loading message
      onHide: null,
      className: '',
      spinnerColor: 'currentColor',
    };

    //
    // Helper functions
    //

    function getComputedPosition(element) {
      if (window.getComputedStyle) {
        return window.getComputedStyle(element).getPropertyValue('position');
      }
      // Fallback for older browsers
      if (element.currentStyle) {
        return element.currentStyle.position;
      }
      return 'static';
    }

    function normalizeTarget(target) {
      if (!target) {
        return document.body;
      }

      if (typeof target === 'string') {
        const element = document.querySelector(target);
        if (!element) {
          console.warn(`LoadingOverlayManager: target "${target}" not found, using body`);
          return document.body;
        }
        return element;
      }

      if (target instanceof Element) {
        return target;
      }

      return document.body;
    }

    //
    // Overlay creation
    //

    function createOverlayElement(opts, id) {
      const overlay = document.createElement('div');
      overlay.classList.add('loading-overlay');
      if (opts.className) {
        overlay.classList.add(opts.className);
      }
      overlay.setAttribute('role', 'status');
      overlay.setAttribute('aria-live', 'polite');
      overlay.setAttribute('aria-busy', 'true');
      overlay.dataset.overlayId = id;

      // Show message
      if (opts.message) {
        const messageEl = document.createElement('div');
        messageEl.classList.add('loading-overlay__message');
        messageEl.innerHTML = opts.message;
        overlay.appendChild(messageEl);
      } else {
        // Create loading icon
        const loadingIcon = createLoadingIcon(opts.spinnerColor);
        overlay.appendChild(loadingIcon);

        // Add visually hidden text for screen readers
        const srOnly = document.createElement('span');
        srOnly.className = 'sr-only';
        srOnly.textContent = 'Loading';
        overlay.appendChild(srOnly);
      }

      return overlay;
    }

    function createLoadingIcon(color) {
      color = color || 'currentColor';

      const loadingIcon = document.createElement('div');
      loadingIcon.classList.add('loading-overlay__icon');
      loadingIcon.setAttribute('aria-hidden', 'true');
      loadingIcon.innerHTML =
        '<svg width="40" height="40" xmlns="http://www.w3.org/2000/svg" stroke="' +
        color +
        '"><g transform="translate(2 2)" stroke-width="4" fill="none" fill-rule="evenodd"><circle stroke-opacity=".5" cx="18" cy="18" r="18" /><path d="M36 18c0-9.94-8.06-18-18-18"></path></g></svg>';

      return loadingIcon;
    }

    //
    // Public methods
    //

    function show(target, options) {
      if (target && !(target instanceof Element) && typeof target !== 'string') {
        console.warn(
          'LoadingOverlayManager: show called without target, use showOnBody() for body overlay or provide a target element/string'
        );

        return null;
      }

      const targetElement = normalizeTarget(target);
      const opts = { ...defaults, ...options };

      // Check if this target already has an overlay
      const existingId = findOverlayByTarget(targetElement);
      if (existingId !== null) {
        console.warn('LoadingOverlayManager: target already has an overlay');
        return existingId;
      }

      const id = nextId++;

      // Store original position style
      const originalPosition = targetElement.style.position;
      const computedPosition = getComputedPosition(targetElement);

      // Set target's position to relative if it's static
      if (computedPosition === 'static') {
        targetElement.style.position = 'relative';
      }

      // Create overlay element
      const overlay = createOverlayElement(opts, id);

      // Set overlay positioning based on target
      if (targetElement === document.body) {
        overlay.style.position = 'fixed';
        overlay.style.height = 'var(--vh, 100vh)';
      } else {
        overlay.style.position = 'absolute';
        overlay.style.height = '100%';
      }

      // Store overlay data
      const overlayData = {
        el: overlay,
        target: targetElement,
        opts,
        timeoutId: null,
        originalPosition,
      };

      overlays.set(id, overlayData);

      // Add overlay to target
      targetElement.appendChild(overlay);
      targetElement.setAttribute('data-loading-overlay', 'open');

      // Trigger reflow for animation
      void overlay.offsetHeight;
      overlay.classList.add('loading-overlay--show');

      // Auto-hide after timeout
      if (opts.timeout > 0) {
        overlayData.timeoutId = setTimeout(() => hide(id), opts.timeout);
      }

      targetElement.dispatchEvent(
        new CustomEvent('LoadingOverlay:open', {
          bubbles: true,
          cancelable: false,
          detail: { id: id, overlayData: overlayData },
        })
      );

      return id;
    }

    function hide(id) {
      // Allow passing target element directly
      if (id instanceof Element) {
        id = findOverlayByTarget(id);
      }

      if (!id) {
        console.warn('LoadingOverlayManager: missing `id` to hide, perhaps use hideAll() instead?');
        return;
      }

      const overlayData = overlays.get(id);
      if (!overlayData) return;

      // Clear auto-hide timeout
      if (overlayData.timeoutId) {
        clearTimeout(overlayData.timeoutId);
      }

      const { el, target, opts, originalPosition } = overlayData;

      // Remove overlay
      target.removeAttribute('data-loading-overlay');

      // Animate out
      el.classList.remove('loading-overlay--show');
      el.classList.add('loading-overlay--hide');

      // Remove from DOM after animation
      setTimeout(() => {
        if (el.parentNode) {
          el.parentNode.removeChild(el);
        }

        // Restore original position style
        if (originalPosition === '') {
          target.style.removeProperty('position');
        } else {
          target.style.position = originalPosition;
        }

        // Call onHide callback
        if (typeof opts.onHide === 'function') {
          opts.onHide(id);
        }

        overlays.delete(id);

        target.dispatchEvent(
          new CustomEvent('LoadingOverlay:hide', {
            bubbles: true,
            cancelable: false,
            detail: { id: id, target: target },
          })
        );
      }, animationDuration);
    }

    function hideAll() {
      const ids = Array.from(overlays.keys());
      ids.forEach((id) => hide(id));
    }

    function isActive(target) {
      const targetElement = normalizeTarget(target);
      return findOverlayByTarget(targetElement) !== null;
    }

    function updateMessage(id, message) {
      // Allow passing target element directly
      if (id instanceof Element) {
        id = findOverlayByTarget(id);
      }

      if (id == null) return;

      const overlayData = overlays.get(id);
      if (!overlayData) return;

      let messageEl = overlayData.el.querySelector('.loading-overlay__message');
      let loadingIconEl = overlayData.el.querySelector('.loading-overlay__icon');

      if (message) {
        if (loadingIconEl) {
          // Remove loading icon if message is set
          loadingIconEl.remove();
        }

        if (!messageEl) {
          // Create message element if it doesn't exist
          messageEl = document.createElement('div');
          messageEl.classList.add('loading-overlay__message');
          overlayData.el.appendChild(messageEl);
        }
        messageEl.innerHTML = message;
      } else {
        // Create loading icon
        const loadingIcon = createLoadingIcon(overlayData.opts.spinnerColor);
        overlayData.el.appendChild(loadingIcon);

        if (messageEl) {
          messageEl.remove();
        }
      }
    }

    //
    // Helper methods
    //

    function findOverlayByTarget(targetElement) {
      for (const [id, data] of overlays.entries()) {
        if (data.target === targetElement) {
          return id;
        }
      }
      return null;
    }

    //
    // Convenience methods
    //

    function showOnBody(options = {}) {
      return show(document.body, options);
    }

    function hideFromBody() {
      const id = findOverlayByTarget(document.body);
      if (id !== null) {
        hide(id);
      }
    }

    //
    // Initialization for declarative usage
    //

    function init() {
      document.dispatchEvent(
        new CustomEvent('LoadingOverlayManager:init', {
          bubbles: true,
          cancelable: false,
        })
      );
    }

    // // Auto-initialize event listeners
    // if (typeof document !== 'undefined') {
    //   if (document.readyState === 'loading') {
    //     document.addEventListener('DOMContentLoaded', init);
    //   } else {
    //     init();
    //   }
    // }

    init();

    return {
      show,
      hide,
      hideAll,
      isActive,
      updateMessage,
      showOnBody,
      hideFromBody,
    };
  })();

  // UMD export pattern - works in Node.js and browsers
  if (typeof module !== 'undefined' && typeof module.exports !== 'undefined') {
    module.exports = LoadingOverlayManager;
  } else if (typeof window !== 'undefined') {
    window.LoadingOverlayManager = LoadingOverlayManager;
  }
})();
